import { wait } from 'shared/utils/wait';
import { ensureAuthenticatedConfig } from '../config';
import { HeadersData } from '../factories/types';

export enum ProcessesConfigStatus {
  ready = 'ready',
  /** Authenticated, but no project selected yet (project selection view). */
  noProject = 'noProject',
  /** No usable config at all, e.g. token refresh failed after a SW restart. */
  unavailable = 'unavailable',
}

export type ProcessesConfigResult =
  | { status: ProcessesConfigStatus.ready; setup: HeadersData }
  | { status: ProcessesConfigStatus.noProject }
  | { status: ProcessesConfigStatus.unavailable };

export function isProcessesConfigReady(
  result: ProcessesConfigResult
): result is { status: ProcessesConfigStatus.ready; setup: HeadersData } {
  return result.status === ProcessesConfigStatus.ready;
}

export function isProcessesConfigWithoutProject(
  result: ProcessesConfigResult
): result is { status: ProcessesConfigStatus.noProject } {
  return result.status === ProcessesConfigStatus.noProject;
}

/** Polls the fetch config until it has a projectId (max `maxRetries × delayMs`). */
export async function waitForConfig(
  maxRetries = 30,
  delayMs = 200
): Promise<ProcessesConfigResult> {
  let hasBaseConfig = false;

  for (let i = 0; i < maxRetries; i++) {
    const setup = await ensureAuthenticatedConfig();
    hasBaseConfig = Boolean(setup?.api);

    if (setup?.api && setup?.projectId) {
      return {
        status: ProcessesConfigStatus.ready,
        setup: {
          api: setup.api,
          projectId: setup.projectId,
          locale: setup.locale,
        },
      };
    }

    await wait(delayMs);
  }

  return {
    status: hasBaseConfig
      ? ProcessesConfigStatus.noProject
      : ProcessesConfigStatus.unavailable,
  };
}
